import { Button, Divider } from 'antd';
import { App, Currency } from 'hydra-data-types';
import React, { FunctionComponent, useState } from 'react';
import styled from 'styled-components';
import {
  ASLClinic,
  getAllScreens,
  MEDICARE_ACCESS_ENTRY,
  UrlKey,
} from '../../config/AssessmentUtils';
import { chooseAssessmentConfig } from '../../config/ChooseAssessmentConfig';
import { AssessmentScreen } from '../../config/FlowTypes';
import { skipToPage } from '../../helpers/SkipToPage';
import { ActionType } from '../../store/ActionTypes';
import { store, useAppStore } from '../../store/Store';
import { Environment } from '../../util/Environment';
import { ConfigToolbox } from './ConfigToolbox';
import { ExperimentControls } from './ExperimentControls';

export const DevelopmentToolbox: FunctionComponent = () => {
  const [expanded, setExpanded] = useState(false);
  const [selectedPage, setSelectedPage] = useState('');
  const [sortPages, setSortPages] = useState(false);
  const flowConfig = useAppStore(chooseAssessmentConfig);
  const allPages = getAllScreens(flowConfig);

  const handlePageChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedPage(event.target.value);
  };

  const SortPagesCheckbox: FunctionComponent = () => (
    <>
      <input
        name="sortpagescb"
        type="checkbox"
        checked={sortPages}
        onChange={() => setSortPages(!sortPages)}
      />
      <label htmlFor="sortpagescb" onClick={() => setSortPages(!sortPages)}>
        &nbsp;Sort Page Names Alphabetically
      </label>
    </>
  );

  return (
    <Toolbox>
      <ToolboxDrawer expanded={expanded}>
        <ToolboxControls onClick={() => setExpanded(!expanded)} data-test="devToolboxHandle">
          II
        </ToolboxControls>
        <ToolboxContent>
          <ConfigToolbox />
          <SortPagesCheckbox />
          <Select value={selectedPage} onChange={handlePageChange} data-test="devSkipTo">
            {(sortPages ? allPages.sort() : allPages).map((screen, index) => {
              return (
                <Option key={index} value={screen}>
                  {screen}
                </Option>
              );
            })}
          </Select>

          <SkipButton page={selectedPage as AssessmentScreen} label={`${selectedPage}`} />

          <Divider />
          {Environment.APP === App.Nerva && <MockReferralUserUrl />}
          <Divider />

          <Divider />
          {Environment.APP === App.Nerva && <MockASLUserUrl />}
          <Divider />

          {Environment.APP === App.Nerva && <MockMedicareAccessUserCountry />}
          <Divider />

          <QuickParamAccess />
          <Divider />

          <ExperimentControls />
        </ToolboxContent>
      </ToolboxDrawer>
    </Toolbox>
  );
};

const SkipButton: FunctionComponent<{
  page: AssessmentScreen;
  label: string;
}> = (props) => {
  const [loading, setLoading] = useState(false);

  async function skip() {
    setLoading(true);
    await skipToPage(props.page);
    setLoading(false);
  }

  return (
    <RoundButton data-test={`devSkipTo-${props.page}`} onClick={skip}>
      {loading ? 'Mocking data...' : `Skip to ${props.label} screen`}
    </RoundButton>
  );
};

const MockReferralUserUrl = () => {
  function handleClick() {
    const searchParams = new URLSearchParams(location.search);

    searchParams.set(UrlKey.REFERRAL_ID, '1');
    searchParams.set(UrlKey.COUPON, 'VGZK25');
    searchParams.set('utm_campaign', 'hipaaform');
    searchParams.set(UrlKey.HCP, 'Dr Test');
    searchParams.set(UrlKey.HCP_CLINIC, 'Test Clinic');

    window.location.search = searchParams.toString();
  }

  return <RoundButton onClick={handleClick}>Mock referral user URL</RoundButton>;
};

const MockASLUserUrl = () => {
  function handleClick() {
    const searchParams = new URLSearchParams(location.search);

    searchParams.set(UrlKey.REFERRAL_ID, '1');
    searchParams.set(UrlKey.ASL, ASLClinic.ProdTest);

    window.location.search = searchParams.toString();
  }

  return <RoundButton onClick={handleClick}>Mock ASL user URL</RoundButton>;
};

const MockMedicareAccessUserCountry = () => (
  <RoundButton
    onClick={() =>
      store.dispatch({
        type: ActionType.SET_ENTRY,
        entry: { ...store.getState().entry, country: 'US', currency: Currency.USD },
      })
    }
  >
    Change User Country to US
  </RoundButton>
);

const QuickParamAccess = () => {
  function setParams(params: Record<string, string>) {
    const searchParams = new URLSearchParams(location.search);

    // Clear any existing related params first
    const allKeys = Array.from(searchParams.keys());
    Object.keys(params).forEach((key) => {
      if (allKeys.includes(key)) {
        searchParams.delete(key);
      }
    });

    // Set the new params
    Object.entries(params).forEach(([key, value]) => {
      searchParams.set(key, value);
    });

    window.location.search = searchParams.toString();
  }

  function clearAllParams() {
    window.location.href = window.location.origin + window.location.pathname.replace('/start', '');
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      <div style={{ fontSize: '12px', fontWeight: 'bold', marginBottom: '4px' }}>
        Quick Param Access:
      </div>

      {/* Trial & Testing */}
      <RoundButton onClick={() => setParams({ [UrlKey.FREE_TRIAL]: 'true' })}>
        Free Trial Mode
      </RoundButton>
      <RoundButton onClick={() => setParams({ testing: 'true', skipAnimations: 'true' })}>
        Testing Mode
      </RoundButton>

      {/* Coupon Codes */}
      <RoundButton onClick={() => setParams({ [UrlKey.COUPON]: 'ZJ4LQ50' })}>
        Coupon: ZJ4LQ50
      </RoundButton>
      <RoundButton onClick={() => setParams({ [UrlKey.COUPON]: 'TEST123' })}>
        Coupon: TEST123
      </RoundButton>

      {/* ASL Clinics */}
      <RoundButton
        onClick={() =>
          setParams({
            [UrlKey.ASL]: ASLClinic.ProdTest,
            [UrlKey.REFERRAL_ID]: '1',
          })
        }
      >
        ASL: Prod Test
      </RoundButton>
      <RoundButton
        onClick={() =>
          setParams({
            [UrlKey.ASL]: ASLClinic.Parsley,
            [UrlKey.REFERRAL_ID]: '1',
          })
        }
      >
        ASL: Parsley
      </RoundButton>

      {/* Medicare Access 65+ re-engagement entry */}
      <RoundButton onClick={() => setParams({ [UrlKey.ENTRY]: MEDICARE_ACCESS_ENTRY })}>
        Entry: Medicare Access
      </RoundButton>

      {/* Demo/Development */}
      <RoundButton onClick={() => setParams({ demo: 'true' })}>Demo Screens</RoundButton>

      {/* Clear */}
      <RoundButton onClick={clearAllParams} style={{ backgroundColor: '#ff4444', color: 'white' }}>
        Clear All Params
      </RoundButton>
    </div>
  );
};

const Toolbox = styled.div`
  position: absolute;
  right: 0;
  display: flex;
  align-items: center;
  height: 100vh;
  z-index: 999;
`;

const ToolboxDrawer = styled.div<{ expanded: boolean }>`
  z-index: 1;
  display: flex;
  background-color: white;
  border: solid gray;
  border-width: ${({ theme }) =>
    `${theme.spacing._2px} 0 ${theme.spacing._2px} ${theme.spacing._2px}`};
  ${({ theme }) => theme.shadow.drop.gray};
  overflow: hidden;
  width: ${({ expanded }) => (expanded ? 300 : 26)}px;
  min-height: ${({ theme }) => theme.spacing._80px};
  border-radius: 16px 0 0 16px;
  transition: all 350ms ease-in-out;
`;

const ToolboxControls = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 26px;
  min-width: 26px;
  margin: 0;
  cursor: pointer;
  color: gray;
`;

const ToolboxContent = styled.div`
  padding: ${({ theme }) =>
    `${theme.spacing._16px} ${theme.spacing._16px} ${theme.spacing._16px} 0`};
  width: 274px;
  max-height: 300px;
  overflow-y: scroll;
`;

const RoundButton = styled(Button).attrs({
  type: 'primary',
  shape: 'round',
})`
  width: 100%;
`;

const Select = styled.select`
  display: block;
  width: 100%;
  padding: ${({ theme }) => theme.spacing._8px};
  margin-bottom: ${({ theme }) => theme.spacing._8px};
  border: 1px solid #d9d9d9;
  border-radius: ${({ theme }) => theme.spacing._4px};
  background-color: white;
`;
const Option = styled.option``;
